/**
 * Utility to parse errors from supabase.functions.invoke() calls.
 * 
 * The Supabase client returns FunctionsHttpError for non-2xx responses,
 * but the actual error body is in the raw Response object, not parsed automatically.
 * This utility extracts the real error message and any additional fields like `blockedUntil`.
 */

export interface ParsedFunctionError {
  message: string;
  blockedUntil?: string;
  status?: number;
  raw?: unknown;
}

/**
 * Parse an error from supabase.functions.invoke()
 * 
 * @param error - The error object from the invoke call
 * @param response - The raw Response object (if available)
 * @returns Parsed error with message, blockedUntil, status
 */
export async function parseInvokeError(
  error: unknown,
  response?: Response | null
): Promise<ParsedFunctionError> {
  // Default fallback
  const defaultMessage = error instanceof Error ? error.message : 'Unknown error';
  
  // If no response, just use the error message
  if (!response) {
    return { message: defaultMessage };
  }
  
  try {
    const contentType = response.headers.get('content-type') || '';
    let body: unknown;
    
    // Clone response to avoid "body already consumed" errors
    const clonedResponse = response.clone();
    
    if (contentType.includes('application/json')) {
      body = await clonedResponse.json();
    } else {
      body = await clonedResponse.text();
    }
    
    // Extract error message from body
    let message = defaultMessage;
    let blockedUntil: string | undefined;
    
    if (typeof body === 'object' && body !== null) {
      const bodyObj = body as Record<string, unknown>;
      message = (bodyObj.error as string) || (bodyObj.message as string) || defaultMessage;
      blockedUntil = bodyObj.blockedUntil as string | undefined;
    } else if (typeof body === 'string' && body.length > 0) {
      message = body;
    }
    
    return {
      message,
      blockedUntil,
      status: response.status,
      raw: body
    };
  } catch (parseError) {
    console.error('[parseInvokeError] Failed to parse response:', parseError);
    return { message: defaultMessage, status: response.status };
  }
}

/**
 * Format a user-friendly rate limit message
 */
export function formatRateLimitMessage(blockedUntil?: string): string {
  if (!blockedUntil) {
    return 'Please wait a few minutes and try again.';
  }
  
  try {
    const blockedDate = new Date(blockedUntil);
    const now = new Date();
    const minutesLeft = Math.ceil((blockedDate.getTime() - now.getTime()) / 60000);
    
    if (minutesLeft > 0) {
      return `Please wait ${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''} and try again.`;
    }
    return 'You can try again now.';
  } catch {
    return 'Please wait a few minutes and try again.';
  }
}
